import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  Star, 
  MapPin, 
  Clock, 
  User,
  Send,
  Loader2,
  MessageSquare,
  BadgeCheck,
  Building2,
  MessageCircle,
  Pencil,
  Trash2,
  Camera,
  X,
  Image as ImageIcon,
  Share2,
  Twitter,
  Facebook,
  Link2,
  Check
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Word count helper
const countWords = (text) => {
  return text.trim() ? text.trim().split(/\s+/).length : 0;
};

const MAX_WORDS = 150;

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    green: { label: 'Welcoming', className: 'status-green' },
    yellow: { label: 'Mixed Reviews', className: 'status-yellow' },
    red: { label: 'Flagged', className: 'status-red' },
    neutral: { label: 'No Reviews', className: 'status-neutral' }
  };
  
  const config = statusConfig[status] || statusConfig.neutral;
  
  return (
    <span className={`status-badge ${config.className}`} data-testid="status-badge">
      {config.label}
    </span>
  );
};

// Share buttons component
const ShareButtons = ({ business }) => {
  const [copied, setCopied] = useState(false);
  const [showShare, setShowShare] = useState(false);
  
  const statusEmoji = {
    green: '💚',
    yellow: '💛', 
    red: '🚩',
    neutral: '📍'
  };
  
  const getShareText = () => {
    const emoji = statusEmoji[business.status] || '📍';
    const rating = business.average_rating ? `${business.average_rating.toFixed(1)}⭐` : 'No ratings yet';
    return `${emoji} ${business.name} in ${business.neighborhood}, Brooklyn - ${rating} on Greenish. Know before you go!`;
  };
  
  const getShareUrl = () => {
    // Using the app URL (in production, this would include business ID)
    return `${window.location.origin}/map?business=${business.id}`;
  };
  
  const shareOnTwitter = () => {
    const text = encodeURIComponent(getShareText());
    const url = encodeURIComponent(getShareUrl());
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank', 'width=550,height=420');
  };
  
  const shareOnFacebook = () => {
    const url = encodeURIComponent(getShareUrl());
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'width=550,height=420');
  };
  
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };
  
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: business.name,
          text: getShareText(),
          url: getShareUrl()
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          setShowShare(true);
        }
      }
    } else {
      setShowShare(true);
    }
  };
  
  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="icon"
        className="h-9 w-9"
        onClick={handleNativeShare}
        data-testid="share-button"
      >
        <Share2 className="w-4 h-4" />
      </Button>
      
      {showShare && (
        <div className="absolute right-0 top-full mt-2 bg-card border rounded-lg shadow-lg p-2 z-50 min-w-[160px]" data-testid="share-menu">
          <div className="text-xs text-muted-foreground px-2 py-1 mb-1">Share on</div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 h-9"
            onClick={shareOnTwitter}
            data-testid="share-twitter"
          >
            <Twitter className="w-4 h-4 text-[#1DA1F2]" />
            Twitter / X
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 h-9"
            onClick={shareOnFacebook}
            data-testid="share-facebook"
          >
            <Facebook className="w-4 h-4 text-[#1877F2]" />
            Facebook
          </Button>
          <Separator className="my-1" />
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 h-9"
            onClick={copyLink}
            data-testid="share-copy-link"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 text-green-500" />
                Copied!
              </>
            ) : (
              <>
                <Link2 className="w-4 h-4" />
                Copy Link
              </>
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-xs text-muted-foreground mt-1"
            onClick={() => setShowShare(false)}
          >
            Close
          </Button>
        </div>
      )}
    </div>
  );
};

// Star rating display
const StarDisplay = ({ rating, size = 'sm' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };
  
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizeClasses[size]} ${
            star <= rating 
              ? 'fill-yellow-400 text-yellow-400' 
              : 'text-gray-300'
          }`}
        />
      ))}
    </div>
  );
};

// Interactive star rating for reviews
const StarInput = ({ value, onChange }) => {
  const [hovered, setHovered] = useState(0);
  
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          className="star-button p-1"
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(star)}
          data-testid={`star-${star}`}
        >
          <Star
            className={`w-8 h-8 ${
              star <= (hovered || value)
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300 hover:text-yellow-200'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Review card component with business response, edit/delete, and photo
const ReviewCard = ({ review, onRespond, canRespond, token, currentUserId, onEdit, onDelete }) => {
  const [showResponseForm, setShowResponseForm] = useState(false);
  const [responseText, setResponseText] = useState('');
  const [responding, setResponding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editRating, setEditRating] = useState(review.rating);
  const [editComment, setEditComment] = useState(review.comment);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const isOwner = currentUserId === review.user_id;
  
  const ratingClass = review.rating >= 4 ? 'review-card-green' : 
                      review.rating === 3 ? 'review-card-yellow' : 
                      'review-card-red';

  const handleSubmitResponse = async () => {
    const wordCount = countWords(responseText);
    if (wordCount > MAX_WORDS) {
      toast.error(`Response exceeds ${MAX_WORDS} words`);
      return;
    }
    if (!responseText.trim()) {
      toast.error('Please enter a response');
      return;
    }
    
    setResponding(true);
    try {
      await axios.post(
        `${API}/reviews/${review.id}/respond`,
        { response: responseText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Response posted!');
      setShowResponseForm(false);
      setResponseText('');
      if (onRespond) onRespond();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post response');
    } finally {
      setResponding(false);
    }
  };
  
  const handleSaveEdit = async () => {
    const wordCount = countWords(editComment);
    if (wordCount > MAX_WORDS) {
      toast.error(`Review exceeds ${MAX_WORDS} words`);
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(
        `${API}/reviews/${review.id}`,
        { rating: editRating, comment: editComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Review updated!');
      setIsEditing(false);
      if (onEdit) onEdit();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update review');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/reviews/${review.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Review deleted');
      if (onDelete) onDelete();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete review');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };
  
  return (
    <div className={`review-card bg-muted/30 p-4 rounded-lg ${ratingClass}`} data-testid="review-card">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="w-4 h-4 text-primary" />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <p className="font-medium text-sm">{review.user_name}</p>
              {review.is_verified_local && (
                <span 
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-primary/10 text-primary"
                  title="This user has submitted 5+ reviews"
                  data-testid="verified-local-badge"
                >
                  <BadgeCheck className="w-3 h-3" />
                  Verified Local
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(review.created_at), { addSuffix: true })}
              {review.updated_at && ' (edited)'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isEditing && (
            <div className="flex flex-col items-end gap-0.5">
              <StarDisplay rating={review.rating} size="sm" />
              {(review.comfort_rating || review.safety_rating) && (
                <div className="flex gap-2 text-[10px] text-muted-foreground">
                  {review.comfort_rating && <span>Comfort: {review.comfort_rating}/5</span>}
                  {review.safety_rating && <span>Safety: {review.safety_rating}/5</span>}
                </div>
              )}
            </div>
          )}
          {isOwner && !isEditing && (
            <div className="flex gap-1">
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                onClick={() => setIsEditing(true)}
                data-testid="edit-review-btn"
              >
                <Pencil className="w-3 h-3" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-destructive hover:text-destructive"
                onClick={() => setShowDeleteConfirm(true)}
                data-testid="delete-review-btn"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          )}
        </div>
      </div>
      
      {/* Edit Mode */}
      {isEditing ? (
        <div className="space-y-3 mt-2">
          <StarInput value={editRating} onChange={setEditRating} />
          <Textarea
            value={editComment}
            onChange={(e) => setEditComment(e.target.value)}
            rows={3}
            className="resize-none"
          />
          <div className="flex items-center justify-between">
            <span className={`text-xs ${countWords(editComment) > MAX_WORDS ? 'text-destructive' : 'text-muted-foreground'}`}>
              {countWords(editComment)}/{MAX_WORDS} words
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => {
                setIsEditing(false);
                setEditRating(review.rating);
                setEditComment(review.comment);
              }}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSaveEdit} disabled={saving}>
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <p className="text-sm text-foreground/80 leading-relaxed">{review.comment}</p>
          
          {/* Review Photo */}
          {review.photo_url && (
            <div className="mt-3">
              <img 
                src={review.photo_url} 
                alt="Review photo" 
                className="rounded-lg max-h-48 object-cover"
              />
            </div>
          )}
        </>
      )}
      
      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="mt-3 p-3 bg-destructive/10 rounded-lg">
          <p className="text-sm text-destructive mb-2">Delete this review?</p>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button size="sm" variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Delete'}
            </Button>
          </div>
        </div>
      )}
      
      {/* Business Response */}
      {review.business_response && !isEditing && (
        <div className="mt-3 ml-4 pl-3 border-l-2 border-secondary/30 bg-secondary/5 rounded-r p-3">
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="w-3.5 h-3.5 text-secondary" />
            <span className="text-xs font-semibold text-secondary">Business Response</span>
            {review.business_response_at && (
              <span className="text-xs text-muted-foreground">
                • {formatDistanceToNow(new Date(review.business_response_at), { addSuffix: true })}
              </span>
            )}
          </div>
          <p className="text-sm text-foreground/80">{review.business_response}</p>
        </div>
      )}
      
      {/* Response Form */}
      {canRespond && !review.business_response && !isEditing && (
        <div className="mt-3">
          {showResponseForm ? (
            <div className="space-y-2 bg-muted/20 p-3 rounded">
              <Textarea
                placeholder="Write your response to this review..."
                value={responseText}
                onChange={(e) => setResponseText(e.target.value)}
                rows={2}
                className="text-sm resize-none"
              />
              <div className="flex items-center justify-between">
                <span className={`text-xs ${countWords(responseText) > MAX_WORDS ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {countWords(responseText)}/{MAX_WORDS} words
                </span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowResponseForm(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSubmitResponse}
                    disabled={responding || countWords(responseText) > MAX_WORDS}
                  >
                    {responding ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Post'}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              className="text-xs text-muted-foreground"
              onClick={() => setShowResponseForm(true)}
              data-testid="respond-btn"
            >
              <MessageCircle className="w-3 h-3 mr-1" />
              Respond to this review
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export const BusinessSheet = ({ business, onClose, onReviewAdded }) => {
  const { user, token } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rating, setRating] = useState(0);
  const [comfortRating, setComfortRating] = useState(0);
  const [safetyRating, setSafetyRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (business) {
      fetchReviews();
    }
  }, [business]);

  const fetchReviews = async () => {
    if (!business) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reviews/${business.id}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      // Check file type
      if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
        toast.error('Only JPEG, PNG, WebP, and GIF images are allowed');
        return;
      }
      setSelectedPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const clearPhoto = () => {
    setSelectedPhoto(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!comfortRating || !safetyRating) {
      toast.error('Please rate both comfort and safety');
      return;
    }
    
    // Check word count
    const wordCount = countWords(comment);
    if (wordCount > MAX_WORDS) {
      toast.error(`Review exceeds ${MAX_WORDS} words (currently ${wordCount} words)`);
      return;
    }
    
    // Calculate overall rating as average of comfort and safety
    const overallRating = Math.round((comfortRating + safetyRating) / 2);
    
    setSubmitting(true);
    try {
      // Submit review
      const response = await axios.post(
        `${API}/reviews`,
        { 
          business_id: business.id, 
          rating: overallRating,
          comfort_rating: comfortRating,
          safety_rating: safetyRating,
          comment 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const newReview = response.data;
      
      // Upload photo if selected
      if (selectedPhoto) {
        const formData = new FormData();
        formData.append('photo', selectedPhoto);
        
        try {
          const photoResponse = await axios.post(
            `${API}/reviews/${newReview.id}/photo`,
            formData,
            { 
              headers: { 
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
              } 
            }
          );
          newReview.photo_url = photoResponse.data.photo_url;
        } catch (photoError) {
          console.error('Photo upload failed:', photoError);
          toast.error('Review posted but photo upload failed');
        }
      }
      
      setReviews([newReview, ...reviews]);
      setRating(0);
      setComfortRating(0);
      setSafetyRating(0);
      setComment('');
      clearPhoto();
      toast.success('Review submitted!');
      onReviewAdded();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const isOpen = Boolean(business);

  return (
    <Sheet open={isOpen} onOpenChange={() => onClose()}>
      <SheetContent className="w-full sm:max-w-lg p-0" data-testid="business-sheet">
        {business && (
          <>
            <SheetHeader className="p-6 pb-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <SheetTitle className="text-2xl font-display mb-2">{business.name}</SheetTitle>
                  <StatusBadge status={business.status} />
                </div>
                <ShareButtons business={business} />
              </div>
              
              <div className="flex items-center gap-4 mt-4">
                <div className="flex items-center gap-2">
                  <StarDisplay rating={Math.round(business.average_rating)} size="md" />
                  <span className="font-semibold">{business.average_rating.toFixed(1)}</span>
                </div>
                <span className="text-sm text-muted-foreground">
                  {business.total_reviews} {business.total_reviews === 1 ? 'review' : 'reviews'}
                </span>
              </div>
              
              <div className="flex flex-col gap-2 mt-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>{business.address}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-muted rounded text-xs">{business.category}</span>
                  <span className="px-2 py-0.5 bg-muted rounded text-xs">{business.neighborhood}</span>
                </div>
              </div>
              
              {business.description && (
                <p className="mt-4 text-sm text-foreground/80">{business.description}</p>
              )}
            </SheetHeader>
            
            <Separator />
            
            <ScrollArea className="flex-1 h-[calc(100vh-320px)]">
              <div className="p-6 space-y-6">
                {/* Write Review Section */}
                {user ? (
                  <div className="bg-muted/30 rounded-xl p-4" data-testid="review-form">
                    <h3 className="font-medium mb-3 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Rate This Business
                    </h3>
                    <form onSubmit={handleSubmitReview} className="space-y-4">
                      <div>
                        <label className="text-sm font-medium mb-2 block">
                          1. How comfortable did you feel in this space?
                        </label>
                        <p className="text-xs text-muted-foreground mb-2">1 = Not at all, 5 = Very!</p>
                        <StarInput value={comfortRating} onChange={setComfortRating} />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">
                          2. How safe did you feel in this space?
                        </label>
                        <p className="text-xs text-muted-foreground mb-2">1 = Not at all, 5 = Very!</p>
                        <StarInput value={safetyRating} onChange={setSafetyRating} />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">
                          Would you like to say more? <span className="font-normal text-muted-foreground">(optional)</span>
                        </label>
                        <Textarea
                          placeholder="Share details about your experience..."
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                          rows={3}
                          className="resize-none"
                          data-testid="review-comment"
                        />
                        <div className="flex justify-end mt-1">
                          <span className={`text-xs ${countWords(comment) > MAX_WORDS ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                            {countWords(comment)}/{MAX_WORDS} words
                          </span>
                        </div>
                      </div>
                      
                      {/* Photo Upload */}
                      <div>
                        <label className="text-sm font-medium mb-2 block">
                          Add a photo <span className="font-normal text-muted-foreground">(optional, max 5MB)</span>
                        </label>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          onChange={handlePhotoSelect}
                          className="hidden"
                          data-testid="photo-input"
                        />
                        {photoPreview ? (
                          <div className="relative inline-block">
                            <img 
                              src={photoPreview} 
                              alt="Preview" 
                              className="h-24 rounded-lg object-cover"
                            />
                            <Button
                              type="button"
                              size="icon"
                              variant="destructive"
                              className="absolute -top-2 -right-2 h-6 w-6"
                              onClick={clearPhoto}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            onClick={() => fileInputRef.current?.click()}
                            data-testid="add-photo-btn"
                          >
                            <Camera className="w-4 h-4" />
                            Add Photo
                          </Button>
                        )}
                      </div>
                      
                      <Button 
                        type="submit"
                        className="w-full rounded-full"
                        disabled={submitting || countWords(comment) > MAX_WORDS || !comfortRating || !safetyRating}
                        data-testid="submit-review-btn"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Posting...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Post Rating
                          </>
                        )}
                      </Button>
                    </form>
                  </div>
                ) : (
                  <div className="bg-muted/30 rounded-xl p-4 text-center">
                    <p className="text-sm text-muted-foreground mb-3">
                      Sign in to rate this business
                    </p>
                    <Button variant="outline" className="rounded-full" asChild>
                      <a href="/login">Sign In</a>
                    </Button>
                  </div>
                )}
                
                {/* Reviews List */}
                <div>
                  <h3 className="font-medium mb-4">
                    Community Reviews ({reviews.length})
                  </h3>
                  
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : reviews.length > 0 ? (
                    <div className="space-y-3">
                      {reviews.map((review) => (
                        <ReviewCard 
                          key={review.id} 
                          review={review} 
                          canRespond={!!user}
                          token={token}
                          currentUserId={user?.id}
                          onRespond={fetchReviews}
                          onEdit={fetchReviews}
                          onDelete={fetchReviews}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">No reviews yet. Be the first to rate this business!</p>
                    </div>
                  )}
                </div>
              </div>
            </ScrollArea>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
